/**
 * 天府通 Bridge 初始化
 * 通过 window.cdTft (Android) 或 window.webkit.messageHandlers (iOS) 调用原生方法
 */

// 判断是否是 iOS
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

// 回调 Promise 存储（通过 extra 区分不同调用）
const callbackMap = new Map();

// 生成唯一的 extra ID
let extraIdCounter = 0;
function generateExtraId(method) {
  extraIdCounter += 1;
  return `${method}_${extraIdCounter}_${Date.now()}`;
}

// 初始化全局 TFT 对象
function initGlobalTFT() {
  if (!window.TFT) {
    window.TFT = {};
  }
}

/**
 * 注册方法对应的回调函数到 window.TFT
 * 回调函数名与方法名保持一致，如 onGetLocationResult
 * 通过 extra 参数区分不同的调用
 */
function registerCallback(method) {
  // 生成回调函数名，如 "onGetLocationResult"
  const callbackName = `on${method.charAt(0).toUpperCase()}${method.slice(1)}Result`;

  // 如果已经注册过，则不需要重复注册
  if (window.TFT && window.TFT[callbackName]) {
    return `window.TFT.${callbackName}`;
  }

  // 在 window.TFT 上挂载回调函数
  if (window.TFT) {
    window.TFT[callbackName] = (extra, resultStr) => {
      // eslint-disable-next-line no-console
      console.log(`[TFT Debug] ${callbackName} called, extra:`, extra, 'resultStr:', resultStr);

      // 通过 extra 找到对应的回调
      const callbackItem = callbackMap.get(extra);
      if (!callbackItem) {
        // eslint-disable-next-line no-console
        console.warn('[TFT Debug] No callback found for extra:', extra);
        return;
      }

      // 清除超时定时器
      clearTimeout(callbackItem.timeout);
      // 从 Map 中移除
      callbackMap.delete(extra);

      try {
        const result = typeof resultStr === 'string' ? JSON.parse(resultStr) : resultStr;
        callbackItem.resolve(result);
      } catch (error) {
        callbackItem.reject(error);
      }
    };
  }

  return `window.TFT.${callbackName}`;
}

/**
 * 调用原生方法（统一封装）
 * @param {string} method 方法名
 * @param {string|null} params 参数
 */
function invokeNative(method, params) {
  // eslint-disable-next-line no-console
  console.log(`[TFT Debug] invokeNative called, method: ${method}, params:`, params);

  try {
    if (isIOS()) {
      // iOS: 通过 webkit.messageHandlers 调用
      const handler = ((window.webkit || {}).messageHandlers || {})[method];
      if (handler && typeof handler.postMessage === 'function') {
        handler.postMessage(params);
      }
    } else {
      // Android: 通过 window.cdTft 调用（注意大小写）
      const nativeMethod = (window.cdTft || {})[method];
      if (typeof nativeMethod === 'function') {
        window.cdTft[method](params);
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`[TFT Debug] invokeNative error, method: ${method}`, error);
    throw error;
  }
}

/**
 * 调用原生方法并等待回调
 * callback 与方法名保持一致，通过 extra 区分不同调用
 */
function invokeNativeWithCallback(method, data) {
  return new Promise((resolve, reject) => {
    // 生成唯一的 extra ID
    const extraId = generateExtraId(method);

    // 设置超时
    const timeout = setTimeout(() => {
      if (callbackMap.has(extraId)) {
        callbackMap.delete(extraId);
        reject(new Error(`${method} 调用超时`));
      }
    }, 10000);

    // 存储回调到 Map
    callbackMap.set(extraId, {
      resolve,
      reject,
      timeout,
    });

    // 注册方法对应的回调函数（如果已注册则复用）
    const callbackName = registerCallback(method);

    // 构建参数
    const params = JSON.stringify({
      data,
      callback: callbackName, // 回调函数名，与方法保持一致
      extra: extraId, // 唯一标识，用于区分不同调用
    });

    invokeNative(method, params);
  });
}

// 初始化
initGlobalTFT();

/**
 * 初始化天府通 Bridge 对象
 */
export function initTftBridge() {
  return {
    /**
     * 打开新页面
     * @param {string} url 页面路径
     */
    openNewWebView(url) {
      // eslint-disable-next-line no-console
      console.log('[TFT Debug] openNewWebView called, url:', url);
      const params = JSON.stringify({
        data: {
          path: url,
        },
        callback: null,
        extra: null,
      });
      invokeNative('openNativePath', params);
    },

    /**
     * 返回上一页（模拟物理返回键）
     */
    goBack() {
      // eslint-disable-next-line no-console
      console.log('[TFT Debug] goBack called');
      invokeNative('back', null);
    },

    /**
     * 获取当前位置
     * 响应：{ lat, lng, country, province, city, district, address, cityCode, adCode }
     */
    async getLocation() {
      // eslint-disable-next-line no-console
      console.log('[TFT Debug] getLocation called');

      try {
        const { data: result } = await invokeNativeWithCallback('getLocation', null);
        // eslint-disable-next-line no-console
        console.log('[TFT Debug] getLocation result:', result);

        if (result.lat && result.lng) {
          return {
            longitude: result.lng,
            latitude: result.lat,
          };
        }
        throw new Error('获取位置信息失败');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[TFT Debug] getLocation error:', error);
        throw new Error('获取位置信息失败');
      }
    },

    /**
     * 拨打电话
     * @param {string} phoneNumber 电话号码
     */
    callThePhone(phoneNumber) {
      // eslint-disable-next-line no-console
      console.log('[TFT Debug] callThePhone called, phoneNumber:', phoneNumber);
      const params = JSON.stringify({
        data: {
          tel: phoneNumber,
        },
        callback: null,
        extra: null,
      });
      invokeNative('dial', params);
    },

    /**
     * 关闭当前 WebView
     */
    closeWebView() {
      // eslint-disable-next-line no-console
      console.log('[TFT Debug] closeWebView called');
      invokeNative('mandatoryCloseWeb', null);
    },

    /**
     * 导航栏/状态栏控制
     * @param {boolean} statusBarVisible 状态栏是否显示
     * @param {boolean} titleBarVisible 标题栏是否显示
     */
    navigationBarControl(statusBarVisible, titleBarVisible) {
      // eslint-disable-next-line no-console
      console.log('[TFT Debug] navigationBarControl called, statusBarVisible:', statusBarVisible, 'titleBarVisible:', titleBarVisible);
      const params = JSON.stringify({
        data: {
          statusBarVisible,
          titleBarVisible,
        },
        callback: null,
        extra: null,
      });
      invokeNative('navigationBarControl', params);
    },
  };
}

let tft;

export function getTft() {
  if (!tft) {
    // eslint-disable-next-line no-console
    console.log('[TFT Debug] tft not initialized, calling initTftBridge');
    tft = initTftBridge();
  }
  return tft;
}
